import { Check, Zap, Building, Shield, CreditCard, Loader2, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocale } from "@/contexts/LocaleContext";
import { PromoCodeInput } from "@/components/PromoCodeInput";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { DonationWidget } from "@/components/DonationWidget";

// JP DorksAI is an isolated platform. Even though the Stripe account is
// shared with other projects, this app only renders and checks out its
// own products. Lookup keys are prefixed `jpdorks_` to guarantee no
// price from another app (AI Lawyer, JP Remote PRO, Legal AI, etc.)
// can be matched here.
const JPDORKS_PLAN_NAMES = new Set(["free", "pro", "enterprise"]);

const PLAN_ORDER = ["free", "pro", "enterprise"];

// Maps internal plan name to Stripe price lookup_key
const PLAN_PRICE_IDS: Record<string, string> = {
  pro: "jpdorks_pro_monthly",
  enterprise: "jpdorks_enterprise_monthly",
};

const Pricing = () => {
  const { t } = useLocale();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [checkoutPriceId, setCheckoutPriceId] = useState<string | null>(null);
  const [dbPlans, setDbPlans] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      const { data } = await supabase.from("plans").select("*");
      if (data) {
        const sorted = [...data]
          .filter((p) => JPDORKS_PLAN_NAMES.has(p.name))
          .sort((a, b) => {
            const ai = PLAN_ORDER.indexOf(a.name);
            const bi = PLAN_ORDER.indexOf(b.name);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          });
        setDbPlans(sorted);
      }
      setPlansLoading(false);
    };
    fetchPlans();
  }, []);

  // Show success toast on return from checkout
  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      toast({
        title: "Payment successful",
        description: "Your subscription will activate within a few seconds.",
      });
    }
  }, [searchParams]);

  const planUiConfig: Record<
    string,
    { icon: React.ElementType; description: string; features: string[]; limitations: string[]; highlighted: boolean; period: string }
  > = {
    free: {
      icon: Zap,
      description: "Try the platform with a single daily search",
      period: "/forever",
      highlighted: false,
      features: ["1 search per day", "Basic dork generation", "Copy to clipboard"],
      limitations: ["No advanced options", "No presets or catalog", "No history or saved library", "No export", "No collections", "No optimizer / explainer"],
    },
    pro: {
      icon: Shield,
      description: "Unlimited research for professionals",
      period: "/month",
      highlighted: true,
      features: [
        "Unlimited searches",
        "All categories",
        "Export (JSON, CSV, TXT)",
        "Collections & saved dorks",
        "Priority AI generation",
        "Risk scoring & analysis",
      ],
      limitations: [],
    },
    enterprise: {
      icon: Building,
      description: "Custom deployment for organizations",
      period: "/month",
      highlighted: false,
      features: [
        "Everything in Pro",
        "Unlimited team members",
        "Custom search limits",
        "Full audit access",
        "SLA guarantee",
        "Dedicated support",
      ],
      limitations: [],
    },
  };

  const handleCheckout = async (planName: string) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (planName === "free") return;
    const priceId = PLAN_PRICE_IDS[planName];
    if (!priceId) {
      toast({ title: "Plan unavailable", description: "Please contact support.", variant: "destructive" });
      return;
    }
    setLoadingPlan(planName);
    setCheckoutPriceId(priceId);
    setLoadingPlan(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-dark">
      <PaymentTestModeBanner />
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12 relative z-10">
        {isAdmin && (
          <div className="max-w-2xl mx-auto mb-8 p-4 rounded-xl bg-primary/10 border border-primary/30 text-center">
            <div className="flex items-center justify-center gap-2 text-primary font-bold text-lg mb-1">
              <Shield className="w-5 h-5" />
              {t("pricing.god_mode_title")}
            </div>
            <p className="text-sm text-muted-foreground">{t("pricing.god_mode_desc")}</p>
          </div>
        )}

        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="text-gradient-cyber inline-block">{t("pricing.choose_plan")}</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">{t("pricing.subtitle")}</p>
        </div>

        {checkoutPriceId && (
          <div className="max-w-3xl mx-auto mb-10 glass-card p-4 relative">
            <button
              onClick={() => setCheckoutPriceId(null)}
              className="absolute top-3 right-3 p-2 rounded-md hover:bg-muted/50 z-10"
              aria-label="Close checkout"
            >
              <X className="w-4 h-4" />
            </button>
            <StripeEmbeddedCheckout priceId={checkoutPriceId} />
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plansLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="glass-card p-6 flex flex-col gap-3">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-10 w-full mt-auto" />
                </div>
              ))
            : dbPlans.map((dbPlan) => {
                const ui = planUiConfig[dbPlan.name];
                if (!ui) return null;
                const Icon = ui.icon;
                const price = dbPlan.name === "free" ? "$0" : `$${Number(dbPlan.price_monthly_usd).toFixed(2)}`;

                return (
                  <div
                    key={dbPlan.name}
                    className={`glass-card p-6 flex flex-col ${ui.highlighted ? "border-primary/50 ring-1 ring-primary/30" : ""}`}
                  >
                    {ui.highlighted && (
                      <div className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full self-start mb-4">
                        {t("pricing.most_popular")}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-5 h-5 text-primary" />
                      <h3 className="text-lg font-bold text-foreground">{dbPlan.display_name}</h3>
                    </div>
                    <div className="mb-2">
                      <span className="text-3xl font-bold text-foreground">{price}</span>
                      <span className="text-muted-foreground text-sm">{ui.period}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-6">{ui.description}</p>
                    <ul className="space-y-2 flex-1 mb-6">
                      {ui.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-foreground">
                          <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                      {ui.limitations.map((l) => (
                        <li key={l} className="flex items-start gap-2 text-sm text-muted-foreground/50">
                          <span className="w-4 text-center shrink-0">—</span> {l}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handleCheckout(dbPlan.name)}
                      disabled={isAdmin || loadingPlan === dbPlan.name || dbPlan.name === "free"}
                      className={`w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                        isAdmin
                          ? "bg-gradient-to-r from-primary to-secondary text-primary-foreground opacity-90 cursor-default"
                          : ui.highlighted
                          ? "bg-gradient-to-r from-primary to-secondary text-primary-foreground hover:shadow-neon"
                          : dbPlan.name === "free"
                          ? "bg-muted/30 text-muted-foreground cursor-default"
                          : "bg-muted/50 text-foreground border border-border/50 hover:bg-muted/70"
                      } disabled:opacity-50`}
                    >
                      {loadingPlan === dbPlan.name && <Loader2 className="w-4 h-4 animate-spin" />}
                      {isAdmin
                        ? t("pricing.god_mode_active")
                        : dbPlan.name === "free"
                        ? t("pricing.current_plan")
                        : loadingPlan === dbPlan.name
                        ? t("pricing.creating")
                        : t("pricing.upgrade_to", { plan: dbPlan.display_name })}
                    </button>
                  </div>
                );
              })}
        </div>

        {user && (
          <div className="max-w-md mx-auto mt-8">
            <PromoCodeInput />
          </div>
        )}

        <div className="mt-10">
          <DonationWidget />
        </div>

        <div className="text-center mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground/60">
          <CreditCard className="w-4 h-4" />
          Secure card payments powered by Stripe
        </div>
        <div className="text-center mt-2 text-xs text-muted-foreground/60">
          <Shield className="w-4 h-4 inline mr-1" />
          {t("pricing.educational")}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;
